<template>
  <MultiSelect
    ref="selectRef"
    v-model="selectedOptions"
    :options="preparedOptions"
    :class="$style.container"
    :option-group-children="optionGroupChildren"
    :reset-filter-on-hide="true"
    :max-selected-labels="maxSelectedLabels"
    :display="display"
    :pt="{
      root: {
        'data-sb-automation': $attrs['data-sb-automation'] || 'sb-multiselect-root',
        'data-sb-regression': 'sb-multi-select',
      },
      labelContainer: ({ props }) => ({
        'data-sb-automation': 'sb-multiselect-label-container',
        class: [$style.labelContainer, props.showClear && $style.labelContainerWithShowClear]
      }),
      clearIcon: {
        'data-sb-automation': 'sb-multiselect-clear-icon',
      },
      chipItem: {
        'data-sb-automation': 'sb-multiselect-chip-item',
        class: $style.chip,
      },
      listContainer: {
        'data-sb-automation': 'sb-multiselect-list-container',
      },
      pcChip: {
        'data-sb-automation': 'sb-multiselect-chip',
      },
      optionGroup: {
        'data-sb-automation': 'sb-multiselect-option-group',
      },
      option: {
        'data-sb-automation': 'sb-multiselect-option',
      },
    }"
    @show="toggleDropdown"
    @hide="toggleDropdown"
  >
    <template #dropdownicon>
      <i :class="['sb-icon-caret-down', iconExpanded && $style.iconExpanded]" />
    </template>
    <template #value="scope">
      <slot
        name="value"
        v-bind="scope"
      />
    </template>
    <template #header="scope">
      <slot
        name="header"
        v-bind="scope"
      />
    </template>
    <template #footer="scope">
      <slot
        name="footer"
        v-bind="scope"
      />
    </template>
    <template #option="scope">
      <slot
        name="option"
        v-bind="scope"
      />
    </template>
    <template #empty>
      <slot name="empty" />
    </template>
    <template #emptyfilter>
      <slot name="emptyfilter" />
    </template>
    <template #content="scope">
      <slot
        name="content"
        v-bind="scope"
      />
    </template>
    <template #loader="scope">
      <slot
        name="loader"
        v-bind="scope"
      />
    </template>
    <template #optiongroup="scope">
      <slot
        name="optiongroup"
        v-bind="scope"
      />
    </template>
  </MultiSelect>
</template>

<script setup>
import { MultiSelect } from 'primevue';
import { defineProps, defineModel, computed, ref, defineExpose, nextTick, defineOptions } from 'vue';

defineOptions({
  name: 'SbMultiSelect',
});

const selectedOptions = defineModel({
  default: [],
});

const iconExpanded = ref(false);
const selectRef = ref(null);

const show = () => {
  if (iconExpanded.value) return;
  nextTick(() => {
    selectRef.value?.show();
  });
};

defineExpose({
  show,
});
const toggleDropdown = () => {
  iconExpanded.value = !iconExpanded.value;
};

const props = defineProps({
  /**
    * Options list
    */
  options: {
    type: Array,
    required: true,
  },
  /**
   * Display type
   */
  display: {
    type: String,
  },
  /**
    * Property name to read children options when passing option groups
    */
  optionGroupChildren: {
    type: String,
    default: () => undefined,
  },
  /**
    * Max number of selected labels to be shown on the multiselect selector. Bigger selection will
    * show the selection quantity in the selector. Default value: 10.
    */
  maxSelectedLabels: {
    type: Number,
    default(rawProps) {
      return rawProps.display === 'chip' ? 3 : 10;
    },
  },
});

const preparedOptions = computed(() => {
  if (props.optionGroupChildren) {
    return props.options?.filter((option) => option[props.optionGroupChildren]?.length > 0) || [];
  }
  return props.options;
});

</script>
<style lang="scss" module>
  @use '@src/components/Multiselect/style/sbMultiselect.module.scss';
</style>
